<template>
  <div ref="container" class="absolute left-0 right-0 bottom-0">
    <div
      ref="inidcatorElement"
      class="absolute bottom-0 h-[3px] rounded-t-[3px] bg-primary duration-500 ease-[cubic-bezier(.26,.89,.03,1)]"
      :style="{
        left: indicatorLeft + 'px',
        right: indicatorRight + 'px',
      }"
      :class="[animateIndicator && 'transition-all', !animateIndicator && 'transition-none']"
    ></div>
  </div>
</template>
<script setup lang="ts">
import { useElementSize } from '@vueuse/core'
import { ref, watch } from 'vue'

interface Props {
  activeElement?: HTMLElement
}

const props = defineProps<Props>()

const DEFAULT_WIDTH = 40

const indicatorLeft = ref(0)
const indicatorRight = ref(0)

const isAnimating = ref<'left' | 'right' | null>(null)
const isDelayOver = ref(false)
const container = ref<HTMLDivElement | null>(null)
const { width } = useElementSize(container)
const animateIndicator = ref(false)

watch(
  () => props.activeElement,
  (newElement, oldElement) => {
    if (oldElement && newElement) {
      calculateIndicatorPosition(newElement.offsetLeft - oldElement.offsetLeft)
    }
  },
)

const calculateIndicatorPosition = (distance: number) => {
  const direction = distance > 0 ? 'right' : 'left'
  if (props.activeElement) {
    isAnimating.value = direction
    isDelayOver.value = false
    const delay = 12000 / Math.abs(distance)

    setTimeout(() => {
      isAnimating.value = null
    }, 500 + delay)

    animateIndicator.value = true
    if (direction == 'right') {
      const { right } = calculatePosition()
      indicatorRight.value = right
      setTimeout(() => {
        const { left } = calculatePosition()
        indicatorLeft.value = indicatorLeftDueToScreenWidth.value ?? left
        resetAnimation()
      }, delay)
    }
    if (direction == 'left') {
      const { left } = calculatePosition()
      indicatorLeft.value = left
      setTimeout(() => {
        const { right } = calculatePosition()
        indicatorRight.value = indicatorRightDueToScreenWidth.value ?? right
        resetAnimation()
      }, delay)
    }
    if (direction == null) {
      const { right, left } = calculatePosition()
      animateIndicator.value = false
      indicatorLeft.value = left
      indicatorRight.value = right
    }
  }
}

const resetAnimation = () => {
  isDelayOver.value = true
  indicatorRightDueToScreenWidth.value = null
  indicatorLeftDueToScreenWidth.value = null
}

const calculatePosition = () => {
  let left = 0
  let right = 0
  if (props.activeElement) {
    const elementWidth = props.activeElement.clientWidth
    const elementInnerWidth =
      props.activeElement.querySelector('.tab-content')?.clientWidth ?? DEFAULT_WIDTH
    const offsetLeft = props.activeElement.offsetLeft
    left = offsetLeft + elementWidth / 2 - elementInnerWidth / 2
    right = width.value - offsetLeft - elementWidth / 2 - elementInnerWidth / 2
  }
  return { left, right }
}

const indicatorRightDueToScreenWidth = ref<number | null>(null)
const indicatorLeftDueToScreenWidth = ref<number | null>(null)

watch(width, () => {
  const { left, right } = calculatePosition()
  if (isAnimating.value == 'left') {
    indicatorLeft.value = left
    if (isDelayOver.value) {
      indicatorRight.value = right
    }
    indicatorRightDueToScreenWidth.value = right
  }
  if (isAnimating.value == 'right') {
    indicatorRight.value = right
    if (isDelayOver.value) {
      indicatorLeft.value = left
    }
    indicatorLeftDueToScreenWidth.value = left
  }

  if (isAnimating.value == null) {
    animateIndicator.value = false
    indicatorRight.value = right
    indicatorLeft.value = left
  }
})
</script>
